import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CheckIcon,
  Heading,
  LeafIcon,
  MessageIcon,
  SmolitMark,
  cn,
} from '@openroad/ui';
import { api, formatDay, unwrap } from '../lib/api';
import { useEndSessionOnAuthFailure } from '../lib/session';

/**
 * The notifications screen.
 *
 * Four kinds have an icon in the export — a booking tick, a message, a Smolit
 * spark and an eco leaf. The `NotificationKind` enum has eight, so the rest are
 * mapped onto the nearest of the four rather than left without one.
 */
const ICON = {
  BOOKING_REQUESTED: 'check',
  BOOKING_CONFIRMED: 'check',
  BOOKING_CANCELLED: 'check',
  TRIP_CANCELLED: 'check',
  REVIEW_RECEIVED: 'check',
  MESSAGE_RECEIVED: 'message',
  SMOLIT_SUGGESTION: 'smolit',
  ECO_BADGE_UNLOCKED: 'leaf',
} as const;

const ICON_TONE = {
  check: 'bg-success-tint text-success',
  message: 'bg-moana-tint text-moana',
  smolit: 'bg-moana-tint',
  leaf: 'bg-ngahere-tint text-leaf',
} as const;

/** "12m ago", "1h ago", "Yesterday". */
function relativeTime(iso: string, now = Date.now()): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000);

  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)}h ago`;
  if (minutes < 48 * 60) return 'Yesterday';
  return formatDay(iso);
}

export function NotificationsScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const notifications = useQuery({
    queryKey: ['notifications'],
    queryFn: () => unwrap(api.GET('/notifications', { params: { query: { limit: 30 } } })),
  });

  const expired = useEndSessionOnAuthFailure(notifications.error);

  const markRead = useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/notifications/{id}/read', { params: { path: { id } } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const markAll = useMutation({
    mutationFn: () => unwrap(api.POST('/notifications/read-all')),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  if (expired) return null;

  const items = notifications.data?.items ?? [];
  const unread = notifications.data?.unreadCount ?? 0;

  return (
    <main className="mx-auto max-w-[600px] px-6 pt-7 pb-16">
      <div className="mb-5 flex items-baseline justify-between">
        <Heading level="panel" className="text-display-sm tracking-display">
          Notifications
        </Heading>
        {unread > 0 ? (
          <Button variant="secondary" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
            Mark all read
          </Button>
        ) : null}
      </div>

      {notifications.isPending ? (
        <p className="text-base text-muted">Loading…</p>
      ) : notifications.isError ? (
        <p className="text-base text-muted">We could not load your notifications.</p>
      ) : items.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="m-0 text-base text-muted">
            Nothing yet. Bookings, messages and reviews all turn up here.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-2.5">
          {items.map((item) => {
            const icon = ICON[item.kind];
            const clickable = Boolean(item.target);

            const go = () => {
              if (!item.readAt) markRead.mutate(item.id);
              if (item.target) navigate(item.target);
            };

            return (
              <Card
                key={item.id}
                lift={clickable ? 'ride' : undefined}
                onClick={clickable ? go : undefined}
                className={cn('flex w-full gap-3.5 p-4 text-left')}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex h-10 w-10 flex-none items-center justify-center rounded-[11px]',
                    ICON_TONE[icon],
                  )}
                >
                  {icon === 'check' ? (
                    <CheckIcon size={20} strokeWidth={2} />
                  ) : icon === 'message' ? (
                    <MessageIcon size={20} strokeWidth={1.8} />
                  ) : icon === 'smolit' ? (
                    <SmolitMark size={22} />
                  ) : (
                    <LeafIcon size={20} strokeWidth={1.8} />
                  )}
                </span>

                <span className="flex-1">
                  <span className="flex items-baseline justify-between">
                    <span className="text-body-lg font-bold">{item.title}</span>
                    <span className="text-mini text-subtle">{relativeTime(item.createdAt)}</span>
                  </span>
                  <span className="mt-0.5 block text-body-sm leading-relaxed text-muted">
                    {item.body}
                  </span>
                </span>

                {!item.readAt ? (
                  <span
                    className="mt-1 h-2 w-2 flex-none rounded-full bg-pohutukawa"
                    aria-label="Unread"
                  />
                ) : null}
              </Card>
            );
          })}
        </div>
      )}
    </main>
  );
}
